import { withNamespaces } from 'react-i18next';
import {
    RiAlarmWarningLine,
    RiAlertLine,
    RiClockwiseLine,
    RiEarthLine,
    RiFileWarningLine,
    RiLeafLine,
    RiNewspaperLine,
    RiPlantLine,
    RiShieldCrossLine,
    RiUserStarLine,
} from 'react-icons/ri';
import { Card, CardBody } from 'reactstrap';

import useSupplierOverviewStatisticsWidgets from './hooks/useSupplierOverviewStatisticsWidgets';
import { isFeatureEnabledForActiveModule } from 'src/helpers/module_helper';

const WIDGET_CONFIG = {
    all_risks_found: {
        title: 'All Risks Found',
        icon: RiAlarmWarningLine,
        iconBackgroundColor: '#FFD8D8',
        iconColor: '#822626',
    },
    esg_databases: {
        title: 'ESG Databases',
        icon: RiEarthLine,
        iconBackgroundColor: '#E8F8F3',
        iconColor: '#1CBB8C',
    },
    sanctions: {
        title: 'Sanctions',
        icon: RiAlertLine,
        iconBackgroundColor: '#FFD8D8',
        iconColor: '#822626',
    },
    pep: {
        title: 'PEP',
        icon: RiUserStarLine,
        iconBackgroundColor: '#CBD1FF',
        iconColor: '#4D61FF',
    },
    adverse_news: {
        title: 'Adverse News',
        icon: RiNewspaperLine,
        iconBackgroundColor: '#D9E9FF',
        iconColor: '#2F80ED',
    },
    regulatory_enforcement: {
        title: 'Regulatory Enforcement',
        icon: RiShieldCrossLine,
        iconBackgroundColor: '#FFF4E5',
        iconColor: '#F2994A',
    },
    insolvency_records: {
        title: 'Insolvency Records',
        icon: RiFileWarningLine,
        iconBackgroundColor: '#FFE4D9',
        iconColor: '#FF763F',
    },
    reputational_risk_exposure: {
        title: 'Reputational Risk Exposure',
        icon: RiPlantLine,
        iconBackgroundColor: '#EDE9FE',
        iconColor: '#7C3AED',
    },
    deforestation_risk: {
        title: 'Deforestation Risk',
        icon: RiLeafLine,
        iconBackgroundColor: '#E8F8F3',
        iconColor: '#27AE60',
    },
    ppwr_compliance_status: {
        title: 'PPWR Compliance Status',
        icon: RiShieldCrossLine,
        iconBackgroundColor: '#FFF4E5',
        iconColor: '#F2994A',
    },
    supplier_documents: {
        title: 'Supplier Documents',
        icon: RiFileWarningLine,
        iconBackgroundColor: '#FFE4D9',
        iconColor: '#FF763F',
    },
    packaging_items: {
        title: 'Packaging Items',
        icon: RiPlantLine,
        iconBackgroundColor: '#EDE9FE',
        iconColor: '#7C3AED',
    },
    last_submission: {
        title: 'Last Submission',
        icon: RiClockwiseLine,
        iconBackgroundColor: '#E8F8F3',
        iconColor: '#1CBB8C',
    },
    open_issues: {
        title: 'Open Issues',
        icon: RiAlertLine,
        iconBackgroundColor: '#FFD8D8',
        iconColor: '#822626',
    },
};

const StatisticsWidget = ({
    title,
    value,
    icon: IconComponent,
    iconBackgroundColor,
    iconColor,
    t,
}) => (
    <Card className="overview-statistics-widget h-100">
        <CardBody className="pb-1">
            <div className="d-flex justify-content-between align-items-start">
                <div className="overflow-hidden">
                    <p className="overview-statistics-widget__title mb-2">
                        {t(title)}
                    </p>
                    <h7 className="mb-0">
                        {value}
                    </h7>
                </div>

                <div
                    className="overview-statistics-widget__icon"
                    style={{
                        backgroundColor: iconBackgroundColor,
                        color: iconColor,
                    }}
                >
                    <IconComponent size={24} />
                </div>
            </div>
        </CardBody>
    </Card>
);

const StatisticsWidgetsSkeleton = () => (
    <div className="overview-statistics-widgets__grid">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((item) => (
            <div key={item}>
                <div className="dt-field dt-skeleton" style={{ width: '100%', height: '120px' }} />
            </div>
        ))}
    </div>
);

const StatisticsWidgets = ({
    t,
    supplierId,
}) => {
    const fetchStatisticsWidgetsQuery = useSupplierOverviewStatisticsWidgets({
        supplierId,
        t,
    });

    if (fetchStatisticsWidgetsQuery.isLoading || fetchStatisticsWidgetsQuery.isFetching) {
        return (
            <div className="overview-statistics-widgets mb-4">
                <StatisticsWidgetsSkeleton />
            </div>
        );
    }

    if (fetchStatisticsWidgetsQuery.isError || !fetchStatisticsWidgetsQuery.data) {
        return null;
    }

    const { widgets = [] } = fetchStatisticsWidgetsQuery.data;

    if (!widgets.length) {
        return null;
    }

    const widgetsToDisplay = widgets.filter((widget) => {
        if (!widget.feature_flag || isFeatureEnabledForActiveModule(widget.feature_flag))
            return true;
        return false;
    });

    if (!widgetsToDisplay.length) return null;

    return (
        <div className="overview-statistics-widgets mb-4">
            <div className="overview-statistics-widgets__grid">
                {widgetsToDisplay.map((widget) => {
                    const config = WIDGET_CONFIG[widget.key];

                    if (!config) {
                        return null;
                    }

                    return (
                        <StatisticsWidget
                            key={widget.key}
                            title={config.title}
                            value={widget.value}
                            icon={config.icon}
                            iconBackgroundColor={config.iconBackgroundColor}
                            iconColor={config.iconColor}
                            t={t}
                        />
                    );
                })}
            </div>
        </div>
    );
};

export default withNamespaces()(StatisticsWidgets);
